import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, User, Calendar, Activity, CheckSquare, Square, FileText, Info } from 'lucide-react';
import { PortalTooltip, NotesTooltip } from '../ui/Tooltips';
import { formatDate, formatRelativeTime, Badge } from '../ui/UIComponents';
import ExternalQuickLinksButton from '../ui/ExternalQuickLinksButton';

const ClientTableView = ({ 
    data = [],
  config,
    columns = [],
    onUpdate = () => {},
    staffList = [],
    workflows = {},
    selectedIds = new Set(),
    onSelect = () => {},
    onRowClick = () => {},
    isBulkEditMode = false,
    onSelectAll = () => {},
    sortColumn = 'name',
    sortDirection = 'asc',
    onSort = () => {}
}) => {
  const showSelectionColumn = isBulkEditMode;
  const firstStickyLeftClass = showSelectionColumn ? 'left-[36px]' : 'left-0';

  const renderCell = (row, col) => {
    const val = row[col.key];

    switch(col.type) {
        case 'main':
             return (
             <div className="flex items-start justify-between gap-2 relative group/cell">
              <div className="flex items-center gap-2 min-w-0">
              <PortalTooltip client={row}>
                <button 
                  onClick={() => onRowClick(row)}
                  className="text-left font-medium text-slate-800 hover:text-blue-600 hover:underline decoration-blue-300 underline-offset-2 transition-colors focus:outline-none text-sm truncate"
                >
                  {val || 'Unknown'}
                </button>
              </PortalTooltip>
              {row.notes && (
                 <NotesTooltip notes={row.notes}>
                  <FileText className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                 </NotesTooltip>
              )}
              </div>
              <ExternalQuickLinksButton config={config} record={row} compact className="flex-shrink-0" />
             </div>
             );

        case 'text':
           if (col.key === 'hmrc_last_tax_year_filed') {
             if (!val) {
               return (
                <span className="inline-flex items-center gap-1 text-xs text-slate-600 font-medium">
                  Unknown
                  <span
                    title="No filed-year evidence returned by HMRC for this client."
                    className="inline-flex items-center text-slate-400 hover:text-slate-600 cursor-help"
                  >
                    <Info className="w-3 h-3" />
                  </span>
                </span>
               );
             }
             return <span className="text-xs text-slate-700 font-medium">{val}</span>;
           }
           return <span className="text-xs text-slate-700 font-medium">{val || '-'}</span>;

        case 'date-text':
             return <span className="text-xs text-slate-600">{val ? formatDate(val) : '-'}</span>;

        case 'deadline':
             const { text, color } = formatRelativeTime(val);
             return (
                <div className="flex flex-col">
                    <span className="text-slate-700 font-medium text-xs">
                        {val ? formatDate(val) : '-'}
                    </span>
                    {val && <Badge variant={color.includes('red') ? 'danger' : color.includes('amber') ? 'warning' : 'success'} size="sm" className={`mt-0.5 text-[9px] px-2 py-1 min-w-[70px] justify-center rounded-full shadow-sm ${color}`}>{text}</Badge>}
                </div>
             );
        
        case 'date':
             return <span className="text-xs text-slate-700">{val ? new Date(val).toLocaleDateString('en-GB', {day:'2-digit', month:'2-digit', year:'numeric'}) : '-'}</span>;

        case 'select-month':
             return (
                <div className="relative group min-w-[100px]">
                    <select 
                        value={val || 'Unscheduled'}
                        onChange={(e) => onUpdate(row.id, col.key, e.target.value)}
                        className={`appearance-none bg-transparent border-b border-transparent hover:border-slate-300 text-xs py-1 pr-4 w-full cursor-pointer focus:outline-none focus:border-blue-500 transition-colors ${!val || val === 'Unscheduled' ? 'text-slate-400' : 'text-slate-700 font-medium'}`}
                    >
                        <option value="Unscheduled">Unscheduled</option>
                        {['2026-01','2026-02','2026-03','2026-04','2026-05','2026-06','2026-07','2026-08','2026-09','2026-10','2026-11','2026-12','2027-01','2027-02','2027-03','2027-04','2027-05','2027-06','2027-07','2027-08','2027-09','2027-10','2027-11','2027-12'].map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                    <Calendar className="w-3 h-3 text-slate-300 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none group-hover:text-blue-400"/>
                </div>
             );

        case 'status':
             const steps = (workflows[col.workflow] || ['Not Started', 'In Progress', 'Completed']) || [];
             let statusColor = "text-slate-600";
             const v = (val || '').toLowerCase();
             if (v.includes('filed') || v.includes('completed') || v.includes('approved')) statusColor = "text-emerald-600 font-semibold";
             else if (v.includes('sent') || v.includes('esign') || v.includes('invoice')) statusColor = "text-purple-600 font-medium";
             else if (v.includes('progress') || v.includes('review') || v.includes('received')) statusColor = "text-blue-600 font-medium";
             else if (v.includes('waiting') || v.includes('requested') || v.includes('quer')) statusColor = "text-amber-600 font-medium";

             return (
                <div className="relative group min-w-[120px]">
                    <select 
                        value={val || ''}
                        onChange={(e) => onUpdate(row.id, col.key, e.target.value)}
                        className={`appearance-none bg-transparent border border-slate-200 hover:border-blue-300 rounded px-1.5 text-[10px] py-1 pr-4 w-full cursor-pointer truncate focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all ${statusColor}`}
                    >
                        <option value="" disabled>Set Status...</option>
                        {steps.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Activity className="w-2.5 h-2.5 text-slate-300 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none group-hover:text-blue-400"/>
                </div>
             );

        case 'staff':
             return (
                <div className="relative group min-w-[90px]">
                    <select 
                        value={val || ''}
                        onChange={(e) => onUpdate(row.id, col.key, e.target.value)}
                        className={`appearance-none bg-transparent border-b border-transparent hover:border-slate-300 text-xs py-1 pr-4 w-full cursor-pointer truncate focus:outline-none transition-colors ${val ? 'text-blue-600 font-medium' : 'text-slate-400'}`}
                    >
                        <option value="">Unassigned</option>
                        {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    {!val && <User className="w-3 h-3 text-slate-300 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none group-hover:text-blue-400"/>}
                </div>
             );
        case 'sync-health': {
             const healthMap = {
                 fresh:  { label: 'Fresh',  bg: 'bg-emerald-100 text-emerald-700 border-emerald-200', title: 'Synced within last 24 hours' },
                 stale:  { label: 'Stale',  bg: 'bg-amber-100 text-amber-700 border-amber-200',       title: 'Synced 1–7 days ago' },
                 old:    { label: 'Old',    bg: 'bg-orange-100 text-orange-700 border-orange-200',    title: 'Last synced 7+ days ago' },
                 failed: { label: 'Failed', bg: 'bg-red-100 text-red-700 border-red-200',             title: row.hmrc_last_error || 'HMRC sync failed' },
                 never:  { label: 'Never',  bg: 'bg-slate-100 text-slate-500 border-slate-200',       title: 'Never synced with HMRC' },
             };
             const h = healthMap[val] || healthMap.never;
             return (
                 <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${h.bg}`} title={h.title}>
                     {h.label}
                 </span>
             );
        }

        default: return <span className="text-xs text-slate-700">{val}</span>;
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-auto pb-10">
      <table className="w-full min-w-max">
        <thead className="bg-gradient-to-r from-sky-600 to-blue-700 border-b border-blue-800 shadow-sm">
          <tr>
            {showSelectionColumn && (
              <th className="w-9 px-2 py-2 text-left sticky top-0 left-0 z-50 bg-sky-700 border-r border-blue-800">
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === data.length}
                  onChange={onSelectAll}
                  className="rounded border-slate-300 bg-white text-blue-600 accent-blue-600 focus:ring-blue-500"
                />
              </th>
            )}
            {columns.map((col, colIndex) => {
              const isFirstDataColumn = colIndex === 0;
              return (
                <th
                  key={col.key}
                  className={`px-3 py-2 text-left text-xs font-semibold text-white uppercase tracking-wide sticky top-0 bg-sky-700 ${isFirstDataColumn ? `${firstStickyLeftClass} z-40 border-r border-blue-800 shadow-[1px_0_0_0_#1e3a8a]` : 'z-30'}`}
                >
                  <button
                    onClick={() => onSort(col.key)}
                    className="flex items-center gap-1 text-white/95 hover:text-white transition-colors"
                  >
                    {col.label}
                    {sortColumn === col.key && (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    )}
                    {sortColumn !== col.key && <ArrowUpDown className="w-3 h-3 opacity-50" />}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (showSelectionColumn ? 1 : 0)} className="px-3 py-8 text-center text-slate-500">
                No records found
              </td>
            </tr>
          ) : (
            data.map(row => {
              const isSelected = selectedIds.has(row.id);
              const stickyBgClass = isSelected ? 'bg-blue-50' : 'bg-white';

              return (
              <tr key={row.id} className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                {showSelectionColumn && (
                  <td className={`px-2 py-2 sticky left-0 z-20 border-r border-slate-200 ${stickyBgClass}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelect(row.id)}
                      className="rounded border-slate-300 bg-white text-blue-600 accent-blue-600 focus:ring-blue-500"
                    />
                  </td>
                )}
                {columns.map((col, colIndex) => {
                  const isFirstDataColumn = colIndex === 0;
                  return (
                    <td
                      key={col.key}
                      className={`px-3 py-2 ${isFirstDataColumn ? `sticky ${firstStickyLeftClass} z-10 border-r border-slate-200 shadow-[1px_0_0_0_#e2e8f0] ${stickyBgClass}` : ''}`}
                    >
                      {renderCell(row, col)}
                    </td>
                  );
                })}
              </tr>
            )})
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ClientTableView;